// @ts-check
/** @typedef {import('../types/api').CommentAccount} CommentAccount */
/** @typedef {import('../types/api').CommentDraft} CommentDraft */
/** @typedef {import('../types/api').PostedComment} PostedComment */

import {
  Check,
  CheckCircle,
  Image,
  Loader,
  MessageSquare,
  Plus,
  RefreshCw,
  Search,
  Send,
  Users,
  X,
  XCircle,
} from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import LoadingScreen from '../components/LoadingScreen';
import Modal from '../components/ui/Modal';
import { useToast } from '../context/ToastContext';
import commentService from '../services/commentService';
import { looksLikeVideoUrl } from '../utils/mediaThumbnail';

// ─── Status Maps ──────────────────────────────────────────────────────────────

const DRAFT_STATUS = {
  pending: {
    label: 'Pending',
    bg: 'bg-yellow-100 dark:bg-yellow-900/40',
    text: 'text-yellow-700 dark:text-yellow-300',
  },
  approved: { label: 'Approved', bg: 'bg-green-100 dark:bg-green-900/40', text: 'text-green-700 dark:text-green-300' },
  posted: { label: 'Posted', bg: 'bg-blue-100 dark:bg-blue-900/40', text: 'text-blue-700 dark:text-blue-300' },
  rejected: { label: 'Rejected', bg: 'bg-red-100 dark:bg-red-900/40', text: 'text-red-700 dark:text-red-300' },
};

const StatusBadge = ({ value }) => {
  const info = DRAFT_STATUS[value] || { label: value, bg: 'bg-muted', text: 'text-foreground/80' };
  return <span className={`px-2 py-1 text-xs rounded-full font-medium ${info.bg} ${info.text}`}>{info.label}</span>;
};

// ─── Main Component ───────────────────────────────────────────────────────────

const CommentManagementPage = () => {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState('accounts');
  /** @type {[CommentAccount[], Function]} */
  const [accounts, setAccounts] = useState([]);
  const [posts, setPosts] = useState([]);
  /** @type {[CommentDraft[], Function]} */
  const [drafts, setDrafts] = useState([]);
  /** @type {[PostedComment[], Function]} */
  const [postedComments, setPostedComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPostIds, setSelectedPostIds] = useState(new Set());
  const [selectedDraftIds, setSelectedDraftIds] = useState(new Set());
  const [showAddAccountModal, setShowAddAccountModal] = useState(false);
  const [expandedPostedId, setExpandedPostedId] = useState(null);

  // Filter state for drafts
  const [draftStatusFilter, setDraftStatusFilter] = useState('');
  const [draftAccountFilter, setDraftAccountFilter] = useState('');

  // ─── Data Fetching ────────────────────────────────────────────────────────

  const fetchAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await commentService.getAccounts();
      setAccounts(Array.isArray(data) ? data : []);
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  const fetchDrafts = useCallback(async () => {
    try {
      setLoading(true);
      const filters = {};
      if (draftStatusFilter) filters.status = draftStatusFilter;
      if (draftAccountFilter) filters.target_account_id = draftAccountFilter;
      const data = await commentService.getDrafts(filters);
      setDrafts(Array.isArray(data) ? data : []);
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast, draftStatusFilter, draftAccountFilter]);

  const fetchPostedComments = useCallback(async () => {
    try {
      setLoading(true);
      const data = await commentService.getPosted();
      setPostedComments(Array.isArray(data) ? data : []);
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  // Fetch data when switching tabs
  useEffect(() => {
    if (activeTab === 'accounts') fetchAccounts();
    else if (activeTab === 'drafts') fetchDrafts();
    else if (activeTab === 'posted') fetchPostedComments();
  }, [activeTab, fetchAccounts, fetchDrafts, fetchPostedComments]);

  // Re-fetch drafts when filters change (only if on drafts tab)
  useEffect(() => {
    if (activeTab === 'drafts') fetchDrafts();
  }, [draftStatusFilter, draftAccountFilter, activeTab, fetchDrafts]);

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleFetchPosts = async () => {
    try {
      setLoading(true);
      const data = await commentService.fetchPosts();
      setPosts(Array.isArray(data) ? data : []);
      showToast('Posts fetched successfully', 'success');
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateDrafts = async (postIds) => {
    try {
      setLoading(true);
      await commentService.generateDrafts(postIds);
      showToast('Drafts generated successfully', 'success');
      setSelectedPostIds(new Set());
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async (id) => {
    if (!window.confirm('Delete this account?')) return;
    try {
      await commentService.deleteAccount(id);
      showToast('Account deleted', 'success');
      fetchAccounts();
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  const handleApproveDraft = async (id) => {
    try {
      await commentService.updateDraft(id, { status: 'approved' });
      showToast('Draft approved', 'success');
      fetchDrafts();
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  const handleRejectDraft = async (id) => {
    try {
      await commentService.updateDraft(id, { status: 'rejected' });
      showToast('Draft rejected', 'success');
      fetchDrafts();
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  const handleUpdateDraftText = async (id, newText) => {
    try {
      await commentService.updateDraft(id, { draft_text: newText });
      showToast('Draft updated', 'success');
      fetchDrafts();
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  const handleBulkApprove = async () => {
    if (selectedDraftIds.size === 0) return;
    try {
      setLoading(true);
      await commentService.bulkApprove([...selectedDraftIds], 'approved');
      showToast(`${selectedDraftIds.size} drafts approved`, 'success');
      setSelectedDraftIds(new Set());
      fetchDrafts();
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePostApproved = async () => {
    const approvedIds = drafts.filter((d) => d.status === 'approved').map((d) => d.id);
    if (approvedIds.length === 0) {
      showToast('No approved drafts to post', 'error');
      return;
    }
    try {
      setLoading(true);
      await commentService.postApproved(approvedIds);
      showToast(`${approvedIds.length} comments posted`, 'success');
      fetchDrafts();
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncReplies = async () => {
    try {
      setLoading(true);
      await commentService.syncReplies();
      showToast('Replies synced', 'success');
      fetchPostedComments();
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const togglePostSelection = (postId) => {
    setSelectedPostIds((prev) => {
      const next = new Set(prev);
      if (next.has(postId)) next.delete(postId);
      else next.add(postId);
      return next;
    });
  };

  const toggleDraftSelection = (draftId) => {
    setSelectedDraftIds((prev) => {
      const next = new Set(prev);
      if (next.has(draftId)) next.delete(draftId);
      else next.add(draftId);
      return next;
    });
  };

  const selectAllPendingDrafts = () => {
    const pendingIds = drafts.filter((d) => d.status === 'pending').map((d) => d.id);
    setSelectedDraftIds(new Set(pendingIds));
  };

  // ─── Tabs ─────────────────────────────────────────────────────────────────

  const tabs = [
    { key: 'accounts', label: 'Target Accounts', count: accounts.length },
    { key: 'posts', label: 'Fetched Posts', count: posts.length },
    { key: 'drafts', label: 'Draft Review', count: drafts.length },
    { key: 'posted', label: 'Posted Comments', count: postedComments.length },
  ];

  // ─── Add Account Modal ────────────────────────────────────────────────────

  const CATEGORY_OPTIONS = ['Fashion', 'Luxury', 'Beauty', 'Lifestyle', 'Editorial', 'Events', 'General'];

  const AddAccountModal = () => {
    const [username, setUsername] = useState('');
    const [category, setCategory] = useState('');
    const [saving, setSaving] = useState(false);
    const [searching, setSearching] = useState(false);
    const [searchResult, setSearchResult] = useState(null);
    const [searchError, setSearchError] = useState('');
    // Touched-since-open flag, reset after a successful submit.
    const [isDirty, setIsDirty] = useState(false);

    // X/Cancel dismiss the modal — confirm first if there's anything to lose.
    const handleCloseClick = () => {
      if (isDirty && !window.confirm('You have unsaved changes. Discard them?')) return;
      setShowAddAccountModal(false);
    };

    const handleSearch = async () => {
      const cleaned = username.replace(/^@/, '').trim();
      if (!cleaned) return;
      try {
        setSearching(true);
        setSearchError('');
        setSearchResult(null);
        const data = await commentService.searchAccount(cleaned);
        setSearchResult(data);
        setUsername(data.username || cleaned);
        if (data.suggested_category) {
          setCategory(data.suggested_category);
        }
      } catch (error) {
        setSearchError(error.message || 'Account not found');
      } finally {
        setSearching(false);
      }
    };

    const handleSearchKeyDown = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSearch();
      }
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      const cleaned = username.replace(/^@/, '').trim();
      if (!cleaned) return;
      try {
        setSaving(true);
        await commentService.createAccount({ username: cleaned, category: category || null });
        showToast('Account added', 'success');
        setIsDirty(false);
        setShowAddAccountModal(false);
        fetchAccounts();
      } catch (error) {
        showToast(error.message, 'error');
      } finally {
        setSaving(false);
      }
    };

    const formatFollowers = (count) => {
      if (!count) return '0';
      if (count >= 1_000_000) return (count / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
      if (count >= 1_000) return (count / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
      return count.toString();
    };

    return (
      <Modal
        maxWidth="md"
        onClose={handleCloseClick}
        className="rounded-lg p-6"
        closeOnBackdrop={false}
        hasUnsavedChanges={isDirty}
      >
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="text-lg font-semibold">Add Target Account</h3>
          <button onClick={handleCloseClick} className="p-1 hover:bg-accent rounded-lg">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Username search */}
          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Username</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value);
                  setSearchResult(null);
                  setSearchError('');
                  setIsDirty(true);
                }}
                onKeyDown={handleSearchKeyDown}
                placeholder="@username"
                className="flex-1 border border-input rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-foreground focus:border-transparent"
                required
              />
              <button
                type="button"
                onClick={handleSearch}
                disabled={searching || !username.replace(/^@/, '').trim()}
                className="flex items-center gap-1.5 px-3 py-2 text-sm bg-muted border border-input rounded-lg hover:bg-accent disabled:opacity-50"
              >
                {searching ? <Loader size={14} className="animate-spin" /> : <Search size={14} />}
                Search
              </button>
            </div>
          </div>

          {/* Search error */}
          {searchError && (
            <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/50 border border-red-200 dark:border-red-900 rounded-lg px-3 py-2">
              {searchError}
            </div>
          )}

          {/* Search result preview */}
          {searchResult && (
            <div className="bg-muted/50 border border-border rounded-lg p-3">
              <div className="flex items-start gap-3">
                {searchResult.profile_picture_url ? (
                  <img
                    src={searchResult.profile_picture_url}
                    alt={searchResult.username}
                    className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-accent flex items-center justify-center flex-shrink-0">
                    <Users size={18} className="text-muted-foreground/70" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground">{searchResult.name || searchResult.username}</p>
                  <p className="text-xs text-muted-foreground">@{searchResult.username}</p>
                  <div className="flex gap-3 mt-1 text-xs text-muted-foreground">
                    <span>{formatFollowers(searchResult.followers_count)} followers</span>
                    <span>{formatFollowers(searchResult.media_count)} posts</span>
                  </div>
                  {searchResult.biography && (
                    <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2">{searchResult.biography}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Category dropdown */}
          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Category</label>
            <select
              value={category}
              onChange={(e) => {
                setCategory(e.target.value);
                setIsDirty(true);
              }}
              className="w-full border border-input rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-foreground focus:border-transparent"
            >
              <option value="">Select a category</option>
              {CATEGORY_OPTIONS.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={handleCloseClick}
              className="px-4 py-2 text-sm border border-input rounded-lg hover:bg-muted/50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
            >
              {saving ? 'Adding...' : 'Add Account'}
            </button>
          </div>
        </form>
      </Modal>
    );
  };

  // ─── Tab Content ──────────────────────────────────────────────────────────

  const renderAccountsTab = () => (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-lg font-semibold text-foreground">Target Accounts</h2>
        <button
          onClick={() => setShowAddAccountModal(true)}
          className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 transition text-sm"
        >
          <Plus size={16} />
          Add Account
        </button>
      </div>

      {loading ? (
        <LoadingScreen />
      ) : accounts.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Users size={48} className="mx-auto mb-3 text-muted-foreground/50" />
          <p>No target accounts yet. Add one to get started.</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-lg overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Username</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Category</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Status</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Added</th>
                <th className="text-right px-4 py-3 font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {accounts.map((account) => (
                <tr key={account.id} className="hover:bg-muted/50">
                  <td className="px-4 py-3 font-medium text-foreground">@{account.username}</td>
                  <td className="px-4 py-3 text-muted-foreground">{account.category || '—'}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-2 py-1 text-xs rounded-full font-medium ${account.is_active ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300' : 'bg-muted text-muted-foreground'}`}
                    >
                      {account.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {account.added_at ? new Date(account.added_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => handleDeleteAccount(account.id)}
                      className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-950/70 rounded-lg"
                      title="Delete account"
                    >
                      <XCircle size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderPostsTab = () => (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-lg font-semibold text-foreground">Fetched Posts</h2>
        <div className="flex gap-3">
          {selectedPostIds.size > 0 && (
            <button
              onClick={() => handleGenerateDrafts([...selectedPostIds])}
              disabled={loading}
              className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 transition text-sm disabled:opacity-50"
            >
              <MessageSquare size={16} />
              Generate for Selected ({selectedPostIds.size})
            </button>
          )}
          <button
            onClick={handleFetchPosts}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 border border-input rounded-lg hover:bg-muted/50 text-sm disabled:opacity-50"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Fetch Posts
          </button>
        </div>
      </div>

      {loading && posts.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Loader size={24} className="animate-spin text-muted-foreground/70" />
        </div>
      ) : posts.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Image size={48} className="mx-auto mb-3 text-muted-foreground/50" />
          <p>No posts fetched yet. Click "Fetch Posts" to pull latest posts from target accounts.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {posts.map((post) => (
            <div
              key={post.id}
              className={`bg-card border rounded-lg overflow-hidden hover:shadow-md transition ${selectedPostIds.has(post.id) ? 'border-foreground ring-2 ring-foreground' : 'border-border'}`}
            >
              {post.media_url && (
                <div className="aspect-square bg-muted overflow-hidden">
                  {looksLikeVideoUrl(post.media_url) ? (
                    <video
                      src={post.media_url}
                      muted
                      playsInline
                      preload="metadata"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <img src={post.media_url} alt="" className="w-full h-full object-cover" />
                  )}
                </div>
              )}
              <div className="p-4">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                  <span className="text-sm font-medium text-foreground">@{post.account_username}</span>
                  {post.draft_status && <StatusBadge value={post.draft_status} />}
                </div>
                <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                  {post.caption
                    ? post.caption.substring(0, 100) + (post.caption.length > 100 ? '...' : '')
                    : 'No caption'}
                </p>
                <div className="flex items-center justify-between text-xs text-muted-foreground/70">
                  <span>{post.location || '—'}</span>
                  <div className="flex gap-3">
                    <span>{post.like_count ?? 0} likes</span>
                    <span>{post.comment_count ?? 0} comments</span>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/60">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedPostIds.has(post.id)}
                      onChange={() => togglePostSelection(post.id)}
                      className="rounded-lg border-input"
                    />
                    Select
                  </label>
                  <button
                    onClick={() => handleGenerateDrafts([post.id])}
                    disabled={loading}
                    className="text-sm text-foreground hover:underline disabled:opacity-50"
                  >
                    Generate
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderDraftsTab = () => {
    const pendingDrafts = drafts.filter((d) => d.status === 'pending');
    const approvedCount = drafts.filter((d) => d.status === 'approved').length;

    return (
      <div>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
          <h2 className="text-lg font-semibold text-foreground">Draft Review</h2>
          <div className="flex flex-wrap gap-3">
            {approvedCount > 0 && (
              <button
                onClick={handlePostApproved}
                disabled={loading}
                className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition text-sm disabled:opacity-50"
              >
                <Send size={16} />
                Post All Approved ({approvedCount})
              </button>
            )}
            {selectedDraftIds.size > 0 && (
              <button
                onClick={handleBulkApprove}
                disabled={loading}
                className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 transition text-sm disabled:opacity-50"
              >
                <CheckCircle size={16} />
                Bulk Approve ({selectedDraftIds.size})
              </button>
            )}
            <button
              onClick={fetchDrafts}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 border border-input rounded-lg hover:bg-muted/50 text-sm disabled:opacity-50"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-4">
          <select
            value={draftStatusFilter}
            onChange={(e) => setDraftStatusFilter(e.target.value)}
            className="border border-input rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-foreground focus:border-transparent"
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="posted">Posted</option>
            <option value="rejected">Rejected</option>
          </select>
          <select
            value={draftAccountFilter}
            onChange={(e) => setDraftAccountFilter(e.target.value)}
            className="border border-input rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-foreground focus:border-transparent"
          >
            <option value="">All Accounts</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                @{a.username}
              </option>
            ))}
          </select>
          {pendingDrafts.length > 0 && (
            <button
              onClick={selectAllPendingDrafts}
              className="flex items-center gap-2 px-3 py-2 text-sm border border-input rounded-lg hover:bg-muted/50"
            >
              <Check size={14} />
              Select All Pending ({pendingDrafts.length})
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader size={24} className="animate-spin text-muted-foreground/70" />
          </div>
        ) : drafts.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <MessageSquare size={48} className="mx-auto mb-3 text-muted-foreground/50" />
            <p>No drafts yet. Generate comments from the "Fetched Posts" tab.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {drafts.map((draft) => (
              <DraftCard
                key={draft.id}
                draft={draft}
                isSelected={selectedDraftIds.has(draft.id)}
                onToggleSelect={() => toggleDraftSelection(draft.id)}
                onApprove={() => handleApproveDraft(draft.id)}
                onReject={() => handleRejectDraft(draft.id)}
                onUpdateText={(text) => handleUpdateDraftText(draft.id, text)}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderPostedTab = () => (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-lg font-semibold text-foreground">Posted Comments</h2>
        <button
          onClick={handleSyncReplies}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 border border-input rounded-lg hover:bg-muted/50 text-sm disabled:opacity-50"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Sync Replies
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader size={24} className="animate-spin text-muted-foreground/70" />
        </div>
      ) : postedComments.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Send size={48} className="mx-auto mb-3 text-muted-foreground/50" />
          <p>No posted comments yet.</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-lg overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Comment</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Post Caption</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Account</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Posted At</th>
                <th className="text-right px-4 py-3 font-medium text-muted-foreground">Replies</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {postedComments.map((comment) => (
                <React.Fragment key={comment.id}>
                  <tr
                    className="hover:bg-muted/50 cursor-pointer"
                    onClick={() => setExpandedPostedId(expandedPostedId === comment.id ? null : comment.id)}
                  >
                    <td className="px-4 py-3 text-foreground max-w-xs truncate">{comment.draft_text}</td>
                    <td className="px-4 py-3 text-muted-foreground max-w-xs truncate">
                      {comment.post_caption
                        ? comment.post_caption.substring(0, 80) + (comment.post_caption.length > 80 ? '...' : '')
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">@{comment.account_username || '—'}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {comment.posted_at ? new Date(comment.posted_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="inline-flex items-center gap-1 text-muted-foreground">
                        <MessageSquare size={14} />
                        {comment.replies?.length || 0}
                      </span>
                    </td>
                  </tr>
                  {expandedPostedId === comment.id && comment.replies && comment.replies.length > 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-3 bg-muted/50">
                        <div className="pl-4 border-l-2 border-input space-y-2">
                          {comment.replies.map((reply, idx) => (
                            <div key={idx} className="flex items-start justify-between text-sm">
                              <div>
                                <span className="font-medium text-foreground">@{reply.reply_author}</span>
                                <p className="text-muted-foreground mt-0.5">{reply.reply_text}</p>
                              </div>
                              <span className="text-xs text-muted-foreground/70 whitespace-nowrap ml-4">
                                {reply.like_count ?? 0} likes
                              </span>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Comment Management</h1>
          <p className="text-muted-foreground mt-1">Manage Instagram comment automation and engagement</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-muted rounded-lg p-1 mb-6 overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-shrink-0 whitespace-nowrap flex-1 px-4 py-2.5 text-sm font-medium rounded-lg transition ${
              activeTab === tab.key
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
            {tab.count > 0 && (
              <span
                className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                  activeTab === tab.key ? 'bg-primary text-primary-foreground' : 'bg-accent text-muted-foreground'
                }`}
              >
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'accounts' && renderAccountsTab()}
      {activeTab === 'posts' && renderPostsTab()}
      {activeTab === 'drafts' && renderDraftsTab()}
      {activeTab === 'posted' && renderPostedTab()}

      {/* Modal */}
      {showAddAccountModal && <AddAccountModal />}
    </div>
  );
};

// ─── Draft Card Component ─────────────────────────────────────────────────────

const DraftCard = ({ draft, isSelected, onToggleSelect, onApprove, onReject, onUpdateText }) => {
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(draft.draft_text || '');

  const handleSaveEdit = () => {
    if (editText.trim() && editText !== draft.draft_text) {
      onUpdateText(editText.trim());
    }
    setEditing(false);
  };

  return (
    <div
      className={`bg-card border rounded-lg p-4 ${isSelected ? 'border-foreground ring-2 ring-foreground' : 'border-border'}`}
    >
      <div className="flex gap-4">
        {/* Left: Post info */}
        <div className="flex-shrink-0 w-20">
          {draft.post_media_url ? (
            looksLikeVideoUrl(draft.post_media_url) ? (
              <video
                src={draft.post_media_url}
                muted
                playsInline
                preload="metadata"
                className="w-20 h-20 object-cover rounded-lg"
              />
            ) : (
              <img src={draft.post_media_url} alt="" className="w-20 h-20 object-cover rounded-lg" />
            )
          ) : (
            <div className="w-20 h-20 bg-muted rounded-lg flex items-center justify-center">
              <Image size={20} className="text-muted-foreground/50" />
            </div>
          )}
          <p className="text-xs text-muted-foreground/70 mt-1 truncate">
            {draft.post_caption ? draft.post_caption.substring(0, 40) + '...' : '—'}
          </p>
        </div>

        {/* Right: Draft content */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={onToggleSelect}
                  className="rounded-lg border-input"
                />
              </label>
              <StatusBadge value={draft.status} />
              {draft.account_username && (
                <span className="text-xs text-muted-foreground/70">@{draft.account_username}</span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {draft.status === 'pending' && (
                <>
                  <button
                    onClick={onApprove}
                    className="p-1.5 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-950/70 rounded-lg"
                    title="Approve"
                  >
                    <CheckCircle size={18} />
                  </button>
                  <button
                    onClick={onReject}
                    className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-950/70 rounded-lg"
                    title="Reject"
                  >
                    <XCircle size={18} />
                  </button>
                </>
              )}
            </div>
          </div>

          {editing ? (
            <div className="space-y-2">
              <textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                rows={3}
                className="w-full border border-input rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-foreground focus:border-transparent resize-none"
              />
              <div className="flex gap-2">
                <button
                  onClick={handleSaveEdit}
                  className="px-3 py-1 text-xs bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
                >
                  Save
                </button>
                <button
                  onClick={() => {
                    setEditing(false);
                    setEditText(draft.draft_text || '');
                  }}
                  className="px-3 py-1 text-xs border border-input rounded-md hover:bg-muted/50"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <p
              className="text-sm text-foreground cursor-pointer hover:bg-muted/50 rounded-lg px-2 py-1 -mx-2"
              onClick={() => setEditing(true)}
              title="Click to edit"
            >
              {draft.draft_text || 'No text'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommentManagementPage;
